/**
 * voice-v8 — der Sende-Hänger: Der Strom ist tot, die Seite merkt es nicht.
 * (30.09.2026, Ticket 2634)
 *
 * Martin, 30.09. 20:04 und 22:05, mit Foto: die Karte steht auf „Sende …“,
 * nichts lässt sich wegklicken, *„ich muss alles neu starten“*. Der Server
 * hatte in beiden Fällen alles richtig getan: Aufnahme angenommen, Erkennung
 * 1,1 s, Antwort gerechnet und gespeichert. Die Seite war in den Hintergrund
 * gewechselt (Bildschirmfoto), und der Antwort-Strom (SSE) starb dort still —
 * weder Daten noch Fehler. Die Antwort stand längst im Verlauf.
 *
 * Bis hierher gab es EINEN Schutz: 60 s ohne ein Byte. Nach einer Rückkehr aus
 * dem Hintergrund greift er erst so viele Sekunden später, wie die Seite schon
 * weg war — Martin gab nach 17 s auf und startete neu.
 *
 * ## Drei Dinge
 *
 * 1. NACHFRAGE. Kommt die Seite zurück (Sichtbarkeit, pageshow, focus — oder ein
 *    Zeitsprung im Takt, wenn keines davon kam), fragt sie SOFORT den Verlauf
 *    des Servers: steht die Antwort zu diesem Zug schon da, ist der Zug
 *    erledigt (`onAusVerlauf`), nicht mehr und nicht weniger. Nie wird etwas
 *    neu gesendet: die Aufnahme ist beim Server (seine Kopfzeilen kommen erst
 *    nach dem ganzen Upload), und ein zweites Senden machte dieselbe Arbeit
 *    und dieselbe Handlung noch einmal.
 * 2. KURZER WACHHUND nach der Rückkehr. Ein Strom, der die Zeit im Hintergrund
 *    überlebt hat, liefert sofort, was sich gepuffert hat — oder, wenn im
 *    Hintergrund noch kein Ping fällig war, beim nächsten Ping. Der Server
 *    pingt alle 15 s (server/voice-command.ts, heartbeat; nicht „~25 s“ wie
 *    bisher in den Kommentaren stand). Die Frist: 10 s nach dem späteren von
 *    (Rückkehr, letztes Byte + ein Pingabstand). Ein gesunder Strom hat bis
 *    dahin etwas geliefert; ein toter nie. Ein erstes Byte nach der Rückkehr
 *    beweist ihn — danach gilt wieder die lange Frist.
 * 3. MESSZEILE. Jeder Eingriff schreibt EINE Zeile
 *    `[ptt-haenger] art=… dauer=…ms hidden=…ms ergebnis=…`, nur Zahlen, damit
 *    der nächste Fall im Journal zu zählen ist.
 *
 * ⚠️ Vor jedem „tot“ fragt die Wache den Verlauf noch einmal — auch der
 *    60-s-Fall: Wer „Verbindung unterbrochen“ meldet, während die Antwort längst
 *    dasteht, schickt Martin sonst zum Neu-Sprechen.
 */

import { fetchChatHistory } from "./chat-history-api.js";
import { meldeSpur } from "./mikro-spur.js";

/** Ohne Rückkehr: so lange darf der Strom völlig still sein (wie bisher). */
export const STROM_STILLE_MS = 60_000;
/**
 * Der Pingabstand des Servers — `setInterval(… res.write(": ping\n\n"), 15000)`
 * in server/voice-command.ts. Ein Test hält beide Zahlen zusammen.
 */
export const SERVER_PING_MS = 15_000;
/** Nach der Rückkehr: so viel Luft ab dem Zeitpunkt, an dem ein Byte fällig war. */
export const RUECKKEHR_FRIST_MS = 10_000;
/** Der Takt der Wache. */
export const WACHE_TAKT_MS = 1_000;
/** Ein Takt, der so viel später kommt, hat die Seite schlafen gesehen. */
const ZEITSPRUNG_MS = 12_000;
/** Wie lange die Frage an den Verlauf höchstens dauern darf. */
export const NACHFRAGE_FRIST_MS = 5_000;
/** Uhrenversatz und Sekundenraster der Datenbank (created_at ist sekundengenau). */
export const ANKER_SPIELRAUM_MS = 2_500;

/**
 * Was die Seite sagt, wenn der Strom beendet wurde und die Antwort nicht im
 * Verlauf zu finden war (app.ts, onDone). Ehrlich in beide Richtungen: sie ist
 * vielleicht schon unterwegs, vielleicht nicht — und Martin weiß, was er tun kann.
 */
export const UNTERBROCHEN_OHNE_ANTWORT =
  "Verbindung unterbrochen — die Antwort kommt im Verlauf, falls sie schon unterwegs ist. Bleibt sie aus, sag es bitte noch einmal.";

/**
 * Dieselbe Auskunft, wenn der Server den Text eines langen Diktats schon gespeichert hat (Ereignis `gespeichert`, Ticket 3085):
 * Martin muss ihn nicht noch einmal sprechen. Ersetzt UNTERBROCHEN_OHNE_ANTWORT und die Meldung des Fehlerzweigs — ein zweites
 * Diktat desselben Textes legte eine zweite Zeile an und ließ ihn doppelt im Prompt stehen.
 */
export const UNTERBROCHEN_TEXT_GESPEICHERT =
  "Verbindung unterbrochen — dein Text ist gespeichert, du musst ihn nicht noch einmal sprechen. Die Antwort kommt im Verlauf, falls sie schon unterwegs ist.";

/** Ist das die Meldung, mit der streamPtt einen verstummten Strom beendet? */
export function istAbgerissenerStrom(fehler: string | undefined): boolean {
  return /^verbindung unterbrochen$/i.test((fehler ?? "").trim());
}

type HaengerArt = "resume-verlauf" | "resume-still" | "watchdog";
type HaengerErgebnis = "antwort-gefunden" | "unterbrochen";

/**
 * Ist der Strom tot? Und wenn ja: welche Regel hat es festgestellt?
 *
 * Reine Rechnung. `rueckkehrAm` ist gesetzt, solange nach einer Rückkehr noch
 * kein Byte kam — dann gilt die kurze Frist statt der langen.
 */
export function sendeStromTot(z: {
  jetzt: number;
  letzteAktivitaet: number;
  rueckkehrAm: number | null;
  /** Eigene Fristen (V9: Puls alle 10 s). Fehlt ein Wert, gilt die V8-Vorgabe. */
  fristen?: SendeFristen;
}): "resume-still" | "watchdog" | null {
  const stille = z.fristen?.stilleMs ?? STROM_STILLE_MS;
  const ping = z.fristen?.pingMs ?? SERVER_PING_MS;
  const rueck = z.fristen?.rueckkehrMs ?? RUECKKEHR_FRIST_MS;
  if (z.rueckkehrAm !== null) {
    const faellig = Math.max(z.rueckkehrAm, z.letzteAktivitaet + ping) + rueck;
    return z.jetzt > faellig ? "resume-still" : null;
  }
  return z.jetzt - z.letzteAktivitaet > stille ? "watchdog" : null;
}

/** Optionale Fristen der Wache; V8 übergibt sie nie (Vorgaben: 60 s, 15 s, 10 s). */
export interface SendeFristen {
  stilleMs?: number;
  pingMs?: number;
  rueckkehrMs?: number;
}

/** „2026-09-30 22:05:53“ (SQLite, UTC) oder ISO — beides zu Millisekunden; null, wenn nicht lesbar. */
function zeitVon(s: string): number | null {
  if (!s) return null;
  let iso = s.includes("T") ? s : s.replace(" ", "T");
  if (!/[zZ]$|[+-]\d\d:?\d\d$/.test(iso)) iso += "Z"; // beide Quellen sind UTC
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

export interface VerlaufsZeile {
  role: string;
  createdAt: string;
  content: string;
  /** Werkzeugkarten der Antwort (chat-history-api.ts) — eine reine Rückfrage-Karte hat keinen Text. */
  metadata?: { toolCards?: readonly unknown[] } | null;
}

/**
 * Steht die Antwort zu DIESEM Zug im Verlauf? `ankerMs` ist der Augenblick, in
 * dem der Server die Aufnahme angenommen hat (seine Uhr). Gezählt wird nur,
 * was danach entstand: eine Frage des Nutzers und, dahinter, eine Antwort mit
 * Text. Die Antwort auf den VORIGEN Zug liegt vor dem Anker.
 *
 * Die Zeilen kommen in zeitlicher Reihenfolge (chat.ts, /api/chat/history).
 */
export function antwortImVerlauf(zeilen: readonly VerlaufsZeile[], ankerMs: number): boolean {
  let frageDa = false;
  for (const z of zeilen) {
    const t = zeitVon(z.createdAt);
    if (t === null || t < ankerMs - ANKER_SPIELRAUM_MS) continue;
    if (z.role === "user") frageDa = true;
    // Nachprüfung 01.10.2026: auch eine Antwort nur aus Werkzeugkarten (reine Rückfrage, Ticket 291) ist eine Antwort.
    else if (z.role === "assistant" && frageDa && (z.content.trim() !== "" || (z.metadata?.toolCards?.length ?? 0) > 0)) return true;
  }
  return false;
}

/**
 * Der Anker: die Uhr des Servers beim Annehmen der Aufnahme (Kopfzeile `Date`
 * der Antwort — sie kommt erst nach dem ganzen Upload). Fehlt sie, die eigene
 * Uhr; der Spielraum deckt dann kleine Abweichungen, keine großen.
 */
export function ankerAus(res: { headers?: { get?: (n: string) => string | null } }, eigeneUhr: number): number {
  const d = res.headers?.get?.("Date");
  const t = d ? Date.parse(d) : NaN;
  return Number.isNaN(t) ? eigeneUhr : t;
}

/**
 * Fragt den Verlauf des Servers. true = die Antwort steht da, false = noch
 * nicht, null = nicht zu erfahren (Netz, Zeit, Anmeldung) — dann gilt „nein“.
 */
export async function fragVerlauf(ankerMs: number, frist = NACHFRAGE_FRIST_MS): Promise<boolean | null> {
  const stopp = new AbortController();
  const wecker = window.setTimeout(() => stopp.abort(), frist);
  try {
    const zeilen = await fetchChatHistory({
      limit: 12,
      sinceIso: new Date(ankerMs - ANKER_SPIELRAUM_MS).toISOString(),
      signal: stopp.signal,
    });
    return antwortImVerlauf(zeilen, ankerMs);
  } catch {
    return null;
  } finally {
    window.clearTimeout(wecker);
  }
}

interface SendeWacheAbhaengigkeiten {
  /** Fragt den Verlauf (true = Antwort da). Fehlt es (nur erkennen: kein Verlauf): keine Nachfrage. */
  frageVerlauf?: () => Promise<boolean | null>;
  /** Die Antwort steht im Verlauf — der Zug ist erledigt. Die Wache ist danach aus. */
  beiAntwort: () => void;
  /** Der Strom ist tot und die Antwort nicht zu finden. Die Wache ist danach aus. */
  beiTot: () => void;
  /** Die Messzeile; Vorgabe: `[ptt-haenger] …` ins Protokoll. */
  meldung?: (text: string) => void;
  /** Die Uhr; Vorgabe Date.now. */
  jetzt?: () => number;
  /** Eigene Fristen (Vespera 9.0 mit Puls). Fehlt es: die V8-Vorgaben. */
  fristen?: SendeFristen;
}

interface SendeWache {
  /** Es kam ein Byte. */
  aktivitaet(): void;
  /** Der Strom ist zu Ende (auf welchem Weg auch immer) — nichts mehr prüfen. */
  stopp(): void;
}

/**
 * Die Wache über einen Sende-Strom. Einmal starten, wenn die Aufnahme beim
 * Server ist; `stopp()` beim Ende. Sie entscheidet nichts über den Inhalt —
 * sie sagt nur „Antwort im Verlauf“ oder „tot“, der Aufrufer handelt.
 */
export function starteSendeWache(d: SendeWacheAbhaengigkeiten): SendeWache {
  const jetzt = d.jetzt ?? (() => Date.now());
  const meldung = d.meldung ?? ((t: string): void => meldeSpur("ptt-haenger", t));
  const start = jetzt();
  let letzteAktivitaet = start;
  let letzterTakt = start;
  let letzteRueckkehr = -Infinity;
  /** Gesetzt, solange nach einer Rückkehr noch kein Byte kam. */
  let rueckkehrAm: number | null = null;
  let verdecktSeit: number | null = document.visibilityState === "hidden" ? start : null;
  let verdecktSumme = 0;
  let fertig = false;
  let fragt = false;

  const verdecktMs = (): number => verdecktSumme + (verdecktSeit === null ? 0 : jetzt() - verdecktSeit);
  const zeile = (art: HaengerArt, ergebnis: HaengerErgebnis): void => {
    meldung(`art=${art} dauer=${Math.round(jetzt() - start)}ms hidden=${Math.round(verdecktMs())}ms ergebnis=${ergebnis}`);
  };

  /**
   * Den Verlauf fragen. `beiNein`: bei „noch nicht“ einfach weiter wachen
   * (Rückkehr) oder den Strom für tot erklären (Frist abgelaufen).
   */
  const pruefe = async (art: HaengerArt, beiNein: "weiter" | "tot"): Promise<void> => {
    if (fertig || fragt) return;
    fragt = true;
    const gefragtUm = jetzt();
    let gefunden: boolean | null;
    try {
      gefunden = d.frageVerlauf ? await d.frageVerlauf() : false;
    } catch {
      gefunden = null;
    }
    fragt = false;
    if (fertig) return;
    if (gefunden === true) {
      // Liefert der Strom nach der Rückkehr wieder, schließt er den Zug selbst —
      // mit seinem Live-Text und dem Vorlesen. Die Wache bleibt dann bei ihrer langen Frist.
      if (beiNein === "weiter" && rueckkehrAm === null) return;
      fertig = true;
      stopp();
      zeile(art, "antwort-gefunden");
      d.beiAntwort();
      return;
    }
    if (beiNein === "weiter") return;
    // Hat sich der Strom während der Frage gemeldet, lebt er — nicht abschneiden.
    // (`>=`: die Frage begann, weil das letzte Byte ALT war — ein Byte im selben
    // Millisekundenschritt ist also ein neues.)
    if (letzteAktivitaet >= gefragtUm) return;
    fertig = true;
    stopp();
    zeile(art, "unterbrochen");
    d.beiTot();
  };

  const rueckkehr = (): void => {
    const n = jetzt();
    if (fertig || n - letzteRueckkehr < 3_000) return;
    letzteRueckkehr = n;
    rueckkehrAm = n;
    if (d.frageVerlauf) void pruefe("resume-verlauf", "weiter");
  };

  const zurueck = (): void => {
    if (verdecktSeit === null) return;
    verdecktSumme += jetzt() - verdecktSeit;
    verdecktSeit = null;
    rueckkehr();
  };
  const beiSichtbarkeit = (): void => {
    if (document.visibilityState === "hidden") verdecktSeit ??= jetzt();
    else zurueck();
  };
  const beiPageHide = (): void => {
    verdecktSeit ??= jetzt();
  };

  const takt = (): void => {
    const n = jetzt();
    const luecke = n - letzterTakt;
    letzterTakt = n;
    // Die Seite hat geschlafen, ohne dass ein Sichtbarkeits-Ereignis kam.
    if (luecke > ZEITSPRUNG_MS && n - letzteRueckkehr >= 3_000) {
      // Nachprüfung 01.10.2026: nur gutschreiben, wenn keine Verdeckung läuft — sonst zählt das spätere
      // visibilitychange dieselbe Zeit noch einmal (41 s Schlaf ergaben hidden=82000ms).
      if (verdecktSeit === null) verdecktSumme += luecke;
      rueckkehr();
    }
    const art = sendeStromTot({ jetzt: n, letzteAktivitaet, rueckkehrAm, fristen: d.fristen });
    if (art) void pruefe(art, "tot");
  };

  const timer = window.setInterval(takt, WACHE_TAKT_MS);
  document.addEventListener("visibilitychange", beiSichtbarkeit);
  window.addEventListener("pageshow", zurueck);
  window.addEventListener("focus", zurueck);
  window.addEventListener("pagehide", beiPageHide);

  function stopp(): void {
    fertig = true;
    window.clearInterval(timer);
    document.removeEventListener("visibilitychange", beiSichtbarkeit);
    window.removeEventListener("pageshow", zurueck);
    window.removeEventListener("focus", zurueck);
    window.removeEventListener("pagehide", beiPageHide);
  }

  return {
    aktivitaet() {
      const n = jetzt();
      letzteAktivitaet = n;
      // Ein Byte nach der Rückkehr beweist den Strom: wieder die lange Frist.
      if (rueckkehrAm !== null && n >= rueckkehrAm) rueckkehrAm = null;
    },
    stopp,
  };
}
